<template>
  <div class="auth-form-container">
    <h2>Vahvista rekisteröityminen</h2>
    <p class="confirmation-instructions">
      Vahvista käyttäjänimesi ja syötä sähköpostiisi lähetetty vahvistuskoodi.
    </p>
    <form @submit.prevent="confirmSignUp">
      <input
        v-model="username"
        placeholder="Käyttäjänimi"
        required
        aria-label="Käyttäjänimi"
        aria-describedby="username-help"
      />
      <small id="username-help" class="input-help-text">Syötä käyttäjänimi, jolla rekisteröidyit.</small>

      <input v-model="code" placeholder="Vahvistuskoodi" required />

      <button type="submit" :disabled="isLoading || !username">
         {{ isLoading ? 'Vahvistetaan...' : 'Vahvista tili' }}
      </button>
    </form>
    <div v-if="error" class="error-message">{{ error }}</div>
    <div class="auth-link">
      <a
        href="#"
        @click.prevent="resendConfirmationCode"
        :class="{ 'disabled-link': isResending || !username }"
        :aria-disabled="isResending || !username"
      >
        {{ isResending ? 'Lähetetään...' : 'Lähetä koodi uudelleen' }}
      </a>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { Auth } from 'aws-amplify';

export default {
  name: 'ConfirmSignUp',
  setup() {
    const username = ref('');
    const code = ref('');
    const error = ref('');
    const isLoading = ref(false);
    const isResending = ref(false);
    const router = useRouter();
    const route = useRoute();

    onMounted(() => {
      // Prioritize sessionStorage, fallback to query param for pre-filling
      let storedUsername = null;
      try {
          storedUsername = sessionStorage.getItem('usernameForConfirmation');
      } catch (storageError) {
          console.error("Could not read username from sessionStorage:", storageError);
      }

      if (storedUsername) {
        username.value = storedUsername;
      } else if (route.query.username) {
        username.value = route.query.username;
      }
      // No error shown here if missing, user can just type it in
    });

    const confirmSignUp = async () => {
      // Basic check if username is entered
      if (!username.value) {
          error.value = "Syötä käyttäjänimesi.";
          return;
      }
      error.value = '';
      isLoading.value = true;
      try {
        // Use the username currently in the input field
        await Auth.confirmSignUp(username.value, code.value);

        // Clear sessionStorage item if it exists (no harm if it doesn't)
        try {
           sessionStorage.removeItem('usernameForConfirmation');
        } catch (storageError) {
           console.error("Could not remove username from sessionStorage:", storageError);
        }
        router.push('/sign-in'); // Navigate to sign-in
      } catch (err) {
        // Translate common error messages
        if (err.code === 'CodeMismatchException') {
          error.value = 'Virheellinen vahvistuskoodi. Tarkista koodi ja yritä uudelleen.';
        } else if (err.code === 'UserNotFoundException') {
          error.value = 'Käyttäjänimeä ei löydy. Varmista, että syötit oikean käyttäjänimen.';
        } else if (err.code === 'NotAuthorizedException' && err.message.includes('already confirmed')) {
          error.value = 'Tämä tili on jo vahvistettu. Kirjaudu sisään.';
        } else {
          error.value = err.message || 'Vahvistuksen aikana tapahtui virhe.';
        }
        console.error('Confirmation error:', err);
      } finally {
         isLoading.value = false;
      }
    };

    const resendConfirmationCode = async () => {
        if (!username.value) {
            error.value = 'Syötä käyttäjänimesi lähettääksesi koodin uudelleen.';
            return;
        }
        error.value = '';
        isResending.value = true;
        try {
            // Use the username currently in the input field
            await Auth.resendSignUp(username.value);
            error.value = `Vahvistuskoodi lähetetty uudelleen käyttäjän ${username.value} sähköpostiin.`;
        } catch (err) {
            if (err.code === 'UserNotFoundException') {
                error.value = 'Käyttäjänimeä ei löydy. Varmista, että syötit oikean käyttäjänimen.';
            } else {
                error.value = err.message || 'Vahvistuskoodin uudelleenlähetys epäonnistui.';
            }
            console.error('Resend code error:', err);
        } finally {
            isResending.value = false;
        }
    };

    return {
      username,
      code,
      error,
      isLoading,
      isResending,
      confirmSignUp,
      resendConfirmationCode,
    };
  },
};
</script>

<style scoped>
/* Styles for the form container */
.auth-form-container {
  max-width: 450px;
  margin: 30px auto;
  padding: 30px 40px;
  background-color: #112240;
  border-radius: 8px;
  box-shadow: 0 10px 30px -10px rgba(2, 12, 27, 0.7);
  border: 1px solid #1e3a5f;
}

/* Heading style */
.auth-form-container h2 {
  color: #ffffff;
  text-align: center;
  margin-bottom: 15px;
  font-size: 1.8rem;
  font-weight: 500;
}

/* Instructions text style */
.confirmation-instructions {
    color: #a8b2d1;
    text-align: center;
    font-size: 0.95rem;
    margin-bottom: 25px;
    line-height: 1.5;
}

/* Form layout */
.auth-form-container form {
  display: flex;
  flex-direction: column;
  gap: 5px; /* Reduced gap to bring help text closer */
}

/* Input field styles */
.auth-form-container input[type="text"],
.auth-form-container input[type="email"],
.auth-form-container input[type="password"],
.auth-form-container input:not([type]) {
  background-color: #0a192f;
  border: 1px solid #1e3a5f;
  color: #ccd6f6;
  padding: 12px 15px;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s ease, box-shadow 0.3s ease, background-color 0.3s ease;
  margin-bottom: 15px; /* Add margin below inputs */
}
/* Remove margin from last input in form */
.auth-form-container form input:last-of-type {
    margin-bottom: 0;
}


/* Input field focus styles */
.auth-form-container input[type="text"]:focus,
.auth-form-container input[type="email"]:focus,
.auth-form-container input[type="password"]:focus,
.auth-form-container input:not([type]):focus {
  outline: none;
  border-color: #64ffda; /* Teal border */
  box-shadow: 0 0 0 3px rgba(100, 255, 218, 0.15); /* Teal glow */
}

/* Placeholder text style */
.auth-form-container input::placeholder {
  color: #8892b0;
  opacity: 0.7;
}

/* Optional: Help text style */
.input-help-text {
    font-size: 0.8rem;
    color: #8892b0;
    margin-top: -12px; /* Pull up closer to the input */
    margin-bottom: 15px; /* Add space below help text */
    display: block; /* Ensure it takes its own line */
    padding-left: 2px; /* Align slightly */
}


/* Submit button style */
.auth-form-container button[type="submit"] {
  background-color: #64ffda;
  color: #0a192f;
  border: none;
  padding: 12px 20px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease, opacity 0.3s ease;
  margin-top: 15px; /* Adjusted margin */
}

.auth-form-container button[type="submit"]:hover:not(:disabled) {
  background-color: #52d6c0;
  transform: translateY(-1px);
}

.auth-form-container button[type="submit"]:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Error message style */
.error-message {
  color: #ff6b6b;
  background-color: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.3);
  padding: 10px 15px;
  border-radius: 4px;
  font-size: 0.9rem;
  text-align: center;
  margin-top: 20px;
}

/* Link style */
.auth-link {
    text-align: center;
    margin-top: 25px;
    font-size: 0.9rem;
    color: #a8b2d1;
}

.auth-link a {
    color: #64ffda;
    text-decoration: none;
    font-weight: 500;
    transition: color 0.3s ease, opacity 0.3s ease;
    cursor: pointer;
}

.auth-link a:hover:not(.disabled-link) {
    color: #ffffff;
    text-decoration: underline;
}

.auth-link a.disabled-link {
    color: #8892b0;
    cursor: not-allowed;
    opacity: 0.7;
    text-decoration: none;
}
</style>